import { Form } from '@remix-run/react';
import { type ZodObject, type ZodType, ZodError } from 'zod';
import { createContext, useContext, useEffect, useState } from 'react';
import {
  extend,
  type ExtensionFactory,
  type ImmutableArray,
  type ImmutableObject,
  none,
  type State,
  useHookstate,
} from '@hookstate/core';
import { type Subscribable, subscribable } from '@hookstate/subscribable';
import { v4 } from 'uuid';

type ValidatedFormState = { [Key: string]: string | string[] | ValidatedFormState | ValidatedFormState[] };

type ExtensionData = {
  identifier: string;
};

type ValidatedFormContext = {
  state: State<ValidatedFormState, Subscribable>;
  touched: State<boolean>;
  prefix?: string;
  schema: ZodType | ZodObject<any, any, any, any>;
};

export function extension<S, E>(): ExtensionFactory<S, E, ExtensionData> {
  return () => {
    const ids = new Map<string, string>();

    return {
      onCreate(rootState, _extensionMethods) {
        return {
          identifier(state) {
            let id = ids.get(state.path.join(''));

            if (!id) {
              id = v4();

              ids.set(state.path.join(''), id);
            }

            return id;
          },
        };
      },
    };
  };
}

const context = createContext<ValidatedFormContext | null>(null);

export function useZodFormData() {
  const ctx = require_context();

  return useHookstate(ctx.state).get({ noproxy: true });
}

export function useIsZodFormValid(): true | string {
  const ctx = require_context();

  const scoped = useHookstate(ctx.state).get({ noproxy: true });

  const result = ctx.schema.safeParse(scoped);

  if (result.success) {
    return true;
  }

  return result.error.errors[0].message;
}

export function fieldPath(name: string): string[] {
  return name.split(/[.[\]]/).filter((p) => p !== '');
}

/**
 * The first issue that belongs to a field: one whose path starts with the
 * field's own path. `territory.zone2.minutes` owns
 * ['territory', 'zone2', 'minutes']; a top-level `start_address` still owns
 * everything under it (['start_address', 'city']), as it always did when
 * only the first segment was compared. Before, a nested field name never
 * equalled a first segment, so nested fields never got their errors.
 */
export function zodFieldError(error: ZodError, name: string): string | null {
  const path = fieldPath(name);

  if (path.length === 0) {
    return null;
  }

  const issue = error.errors.find(
    (e) => e.path.length >= path.length && path.every((segment, index) => String(e.path[index]) === segment),
  );

  return issue?.message ?? null;
}

/**
 * Walk a dotted/bracketed field name down the form state.
 *
 * hookstate's `nested()` returns UNDEFINED as soon as the value it is
 * asked to descend into is not an object (missing record, primitive,
 * null) — see the `Object(currentValue) !== currentValue` branch in
 * @hookstate/core. So `rates.photo_pay_0_1000` resolves to undefined
 * whenever the form state has no `rates` record at all, and every
 * caller then blew up on `state.get()` mid-render, taking the whole
 * route down with "Cannot read properties of undefined (reading 'get')".
 *
 * Returns undefined instead of throwing; `useZodFormState` swaps in a
 * safe placeholder so a missing parent can never crash a render.
 */
export function getNestedState(
  state: State<ValidatedFormState, Subscribable>,
  name: string,
): State<any, Subscribable> | undefined {
  const path = fieldPath(name);

  if (path.length === 0) {
    throw new Error('Invalid name');
  }

  let nested: State<any, Subscribable> | undefined = state.nested(path[0]);

  for (let index = 1; index < path.length && nested !== undefined; index += 1) {
    // hookstate types promise a state here; the runtime returns undefined
    // when the value being descended into is not an object.
    nested = nested.nested(path[index]) as unknown as State<any, Subscribable> | undefined;
  }

  return nested;
}

/**
 * Stand-in for a field whose parent record is absent from form state.
 *
 * Reads answer `undefined` (an empty field), and a write materialises
 * the missing ancestors first — on the event/effect that writes, never
 * during render — so the field starts working the moment it is used.
 */
function missingFieldState(
  root: () => State<ValidatedFormState, Subscribable>,
  path: string[],
): State<any, Subscribable> {
  const resolve = (create: boolean): State<any, Subscribable> | undefined => {
    let current: State<any, Subscribable> = root();

    for (let index = 0; index < path.length; index += 1) {
      const value = current.get({ noproxy: true, stealth: true }) as unknown;

      if (Object(value) !== value) {
        if (!create) {
          return undefined;
        }

        current.set({} as any);
      }

      const next = current.nested(path[index]) as State<any, Subscribable> | undefined;

      if (next === undefined) {
        return undefined;
      }

      current = next;
    }

    return current;
  };

  return {
    get: () => undefined,
    set: (value: any) => {
      // Deleting a field that does not exist is a no-op — never
      // materialise ancestors just to remove nothing.
      resolve(value !== none)?.set(value);
    },
    merge: (value: any) => {
      const target = resolve(true);

      if (!target) {
        return;
      }

      const current = target.get({ noproxy: true, stealth: true }) as unknown;

      if (Object(current) !== current) {
        // Merging into a record that is not there yet: create it first.
        target.set((Array.isArray(value) ? [] : {}) as any);
      }

      target.merge(value);
    },
    subscribe: () => () => {
      // nothing to unsubscribe from — the field has no state yet
    },
    path,
  } as unknown as State<any, Subscribable>;
}

function useZodFormState(ctx: ValidatedFormContext, name: string) {
  const prefixed = `${ctx.prefix ?? ''}${name}`;

  const root = useHookstate(ctx.state);

  return { prefixed, state: getNestedState(root, prefixed) ?? missingFieldState(() => root, fieldPath(prefixed)) };
}

function useFieldTouched(ctx: ValidatedFormContext, state: State<any, Subscribable>) {
  const formTouched = useHookstate(ctx.touched).get();

  const [touched, setTouched] = useState(false);

  useEffect(
    state.subscribe(() => {
      setTouched(true);
    }),
    [state],
  );

  return touched || formTouched;
}

export function useZodFormFieldObject<T extends {}>(
  name: string,
): [string, T | undefined, (value: T | undefined | null) => void, string | null, boolean] {
  const ctx = require_context();

  const { state, prefixed } = useZodFormState(ctx, name);

  const scoped = useHookstate(ctx.state).get({ noproxy: true, stealth: true });

  const result = ctx.schema.safeParse(scoped);

  let error: string | null = null;

  if (!result.success && result.error instanceof ZodError) {
    error = zodFieldError(result.error, prefixed);
  }

  const value = state.get();

  return [
    prefixed,
    // eslint-disable-next-line @typescript-eslint/no-base-to-string
    value === '' || value === null || value === undefined ? undefined : (value as T),
    (value: T | undefined | null) => {
      if (value === undefined || value === null) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        state.set(none);
      } else {
        state.merge(value);
      }
    },
    error,
    useFieldTouched(ctx, state),
  ];
}

export function useZodFormFieldSingle<T extends string>(
  name: string,
): [string, T | undefined, (value: string | undefined | null) => void, string | null, boolean] {
  const ctx = require_context();

  const { state, prefixed } = useZodFormState(ctx, name);

  const scoped = useHookstate(ctx.state).get({ noproxy: true, stealth: true });

  const result = ctx.schema.safeParse(scoped);

  let error: string | null = null;

  if (!result.success && result.error instanceof ZodError) {
    error = zodFieldError(result.error, prefixed);
  }

  const value = state.get();

  return [
    prefixed,
    // eslint-disable-next-line @typescript-eslint/no-base-to-string
    value === '' || value === null || value === undefined ? undefined : (value.toString() as T),
    (value: string | undefined | null) => {
      if (value === undefined || value === '' || value === null) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        state.set(none);
      } else {
        state.set(value);
      }
    },
    error,
    useFieldTouched(ctx, state),
  ];
}

export function useZodFormFieldSingleValue<T extends string>(name: string): T | undefined;
export function useZodFormFieldSingleValue<T extends string, R>(name: string, map: (value?: T) => R): R;
export function useZodFormFieldSingleValue<T extends string, R>(
  name: string,
  map?: (value?: T) => R,
): R | T | undefined {
  const [, value] = useZodFormFieldSingle<T>(name);

  return map !== undefined ? map(value) : value;
}

export function useZodFormFieldBooleanValue(name: string): boolean | undefined {
  const [_name, value] = useZodFormFieldSingle(name);

  if (value === undefined || value === null) {
    return undefined;
  }

  return value === 'true';
}

export function useZodFormFieldArray(name: string): [
  Array<{
    name: string;
    remove: () => void;
  }>,
  (value?: Record<string, any>) => void,
  () => void,
  Record<string, unknown>[],
] {
  const ctx = require_context();

  const { state, prefixed } = useZodFormState(ctx, name) as {
    state: State<ValidatedFormState[], Subscribable>;
    prefixed: string;
  };

  const scoped = useHookstate(ctx.state);
  const arrayMissing = !('map' in state);

  useEffect(() => {
    if (!arrayMissing) {
      return;
    }

    const parent = prefixed.split('.');
    const property = parent.pop();

    if (!property) {
      throw new Error('Invalid name');
    }

    if (parent.length == 0) {
      scoped.merge({ [property]: [] });
    } else {
      const parentName = parent.join('.');

      (getNestedState(scoped, parentName) ?? missingFieldState(() => scoped, fieldPath(parentName))).merge({
        [property]: [],
      });
    }
  }, []);

  if (arrayMissing) {
    return [
      [],
      () => {
        throw new Error('Cannot add to state array before state array exists');
      },
      () => {
        // nothing to clear
      },
      [],
    ];
  }

  return [
    state.map((nested, index) => ({
      name: `${prefixed}[${index}]`,
      remove() {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        nested.set(none);
      },
    })),
    (value) => {
      state.merge([value ? { ...value } : ({} as any)]);
    },
    () => {
      state.set([]);
    },
    state.get() as any,
  ];
}

export function useZodFormFieldMultiple(
  name: string,
): [string, string[], (value: string[]) => void, string | null, boolean] {
  const ctx = require_context();

  const { state, prefixed } = useZodFormState(ctx, name);

  const scoped = useHookstate(ctx.state).get({ noproxy: true, stealth: true });

  const result = ctx.schema.safeParse(scoped);

  let error: string | null = null;

  if (!result.success && result.error instanceof ZodError) {
    error = zodFieldError(result.error, prefixed);
  }

  const value = state.get() as ImmutableArray<string>;

  return [
    prefixed,
    Array.from(value || []),
    (value: string[]) => {
      state.set(value);
    },
    error,
    useFieldTouched(ctx, state),
  ];
}

type RecursivePartial<T> = {
  [P in keyof T]?: RecursivePartial<T[P]>;
};

function require_context() {
  const ctx = useContext(context);

  if (!ctx) {
    throw new Error('<NestedFields /> must be used within a <ZodForm />');
  }

  return ctx;
}

export function ZodNestedForm({
  children,
  name,
  inherit = true,
}: {
  children: React.ReactNode;
  name: string;
  inherit?: boolean;
}) {
  const ctx = require_context();

  if (ctx.prefix && name.startsWith(ctx.prefix)) {
    name = name.substring(ctx.prefix.length);
  }

  return (
    <context.Provider value={{ ...ctx, prefix: ctx?.prefix && inherit ? `${ctx.prefix}${name}.` : `${name}.` }}>
      {children}
    </context.Provider>
  );
}

export interface ZodFormProps<T = {}> {
  children: React.ReactNode;
  schema: ZodObject<any, any, any, T> | ZodType<T, any, any>;
  defaultValues?: RecursivePartial<T>;
  onValid?: (data: T) => void;
  onSubmit?: () => void;
  method?: 'GET' | 'POST';
  className?: string;
  action?: string;
  /** With `action`, names the fetcher it submits through, so `useFetcher({ key })` can read its state and reply. */
  fetcherKey?: string;
}

export default function ZodForm<T = {}>(props: ZodFormProps<T>) {
  const state = useHookstate((props.defaultValues ?? {}) as ValidatedFormState, extend(subscribable(), extension()));
  const touched = useHookstate(false);

  useEffect(() => {
    const onChange = (value: ImmutableObject<ValidatedFormState>) => {
      const { onValid } = props;

      if (!onValid) {
        return;
      }

      const result = props.schema.safeParse(value);

      if (result.success) {
        onValid(result.data);
      }
    };

    onChange(state.get({ stealth: true, noproxy: true }));

    return state.subscribe(onChange);
  }, [state, props.onValid]);

  return (
    <context.Provider value={{ state, schema: props.schema, touched }}>
      <Form
        method={props.method ?? 'POST'}
        action={props.action}
        navigate={!props.action}
        fetcherKey={props.action ? props.fetcherKey : undefined}
        autoComplete="off"
        className={props.className ?? 'w-full'}
        onSubmit={(e) => {
          touched.set(true);

          const data = state.get({ noproxy: true, stealth: true });

          const result = props.schema.safeParse(data);

          if (!result.success) {
            e.preventDefault();
          }

          props.onSubmit?.();
        }}
      >
        {props.children}
      </Form>
    </context.Provider>
  );
}
